import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { DateTime } from "luxon";
import { FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist/es/constants";
import storage from 'redux-persist/es/storage';
import { persistReducer, persistStore, PersistedState } from 'redux-persist';
import storageSession from 'redux-persist/es/storage/session';

import { ajaxController } from "../ajax/controller/AjaxController";
import { isOnlineNow } from "../offline/connectivity";
import { refreshToken } from "./actions/authActions";
import { forgetSessionChoice } from "../i18n/languages";
import selectedShiftReducer, { setSelectedDate, setSelectedShift } from './reducers/shiftReducer';
import authReducer, { logout } from './reducers/authReducer';
import viewReducer from './reducers/viewReducer';
import themeReducer from './reducers/themeReducer';
import { shiftLetterToName, shiftNameToLetter } from "../types/Shift";
import dashboardReducer from './reducers/dashboardReducer';

const viewPersistConfig = {
  key: 'view',
  storage: storageSession, // Note: This is per tab
};

const rootReducer = combineReducers({
  authReducer: authReducer,
  shift: selectedShiftReducer,
  view: persistReducer(viewPersistConfig, viewReducer),
  theme: themeReducer,
  dashboard: dashboardReducer
});

// Note: bump this whenever UserDto or the role config changes shape. A refresh renews tokens and
// nothing else, so a signed-in browser would otherwise keep a stale user object and role list for
// the life of its refresh token. A mismatch here discards the slice and sends them back to login.
const PERSIST_VERSION = 5;

const rootPersistConfig = {
  key: 'root',
  storage,
  whitelist: ['authReducer'],
  blacklist: ['view'],
  version: PERSIST_VERSION,
  migrate: (state: PersistedState) =>
    Promise.resolve(state?._persist.version === PERSIST_VERSION ? state : undefined),
};

const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;

const currentShiftState = store.getState().shift
const storedDate = sessionStorage.getItem("shiftDate");
const storedShift = sessionStorage.getItem("shiftDayOrNight");

// Note: only override if redux has its initial defaults (to avoid resetting when navigating back)
if (storedDate && currentShiftState.selectedDate === DateTime.now().toISODate()) {
  store.dispatch(setSelectedDate(storedDate));
}
if (storedShift && currentShiftState.selectedShift === 'DAY') {
  const shiftCode = shiftLetterToName(storedShift);
  store.dispatch(setSelectedShift(shiftCode === "AFTERNOON" ? "DAY" : shiftCode));
}

let prevShiftState = store.getState().shift;
store.subscribe(() => {
  const nextShiftState = store.getState().shift;
  const dateChanged = nextShiftState.selectedDate !== prevShiftState.selectedDate;
  const shiftChanged = nextShiftState.selectedShift !== prevShiftState.selectedShift;

  if (dateChanged || shiftChanged) {
    sessionStorage.setItem("shiftDate", nextShiftState.selectedDate);
    sessionStorage.setItem("shiftDayOrNight", shiftNameToLetter(nextShiftState.selectedShift));
  }
  prevShiftState = nextShiftState;
});

let wasSignedIn = store.getState().authReducer.isAuthenticated;
store.subscribe(() => {
  const signedIn = store.getState().authReducer.isAuthenticated;
  if (wasSignedIn && !signedIn) forgetSessionChoice();
  wasSignedIn = signedIn;
});

ajaxController.setTokenGetter(() => {
  const state = store.getState();
  return state.authReducer;
});

ajaxController.setTokenRefreshCallback(() =>
  store.dispatch(refreshToken(store.getState().authReducer.refreshToken!))
);

ajaxController.setAuthFailureHandler(() => {
  if (!store.getState().authReducer.isAuthenticated) return;

  if (!isOnlineNow()) {
    console.error('Ignoring an unrecoverable auth failure while offline; keeping the session');
    return;
  }

  console.error('Session could not be recovered; signing out');
  store.dispatch(logout());
});